import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd"
import { Box, Flex, Heading, Spinner, Text, VStack } from "@chakra-ui/react"
import { toaster } from "../toaster"
import { useMemo } from "react"
import KanbanCard from "../components/sourcing/KanbanCard"
import { useKanbanCards, useUpdateKanbanStage } from "../hooks/useSourcingQueries"
import { type KanbanCard as KanbanCardType } from "../services/sourcingApi"
import { useUserDetails } from "../hooks/useAuthQueries"

const COLUMNS = [
  { id: "interested", title: "Interested" },
  { id: "due_diligence", title: "Due Diligence" },
  { id: "acquisition", title: "Acquisition" },
  { id: "acquired", title: "Acquired" },
  { id: "missed_out", title: "Missed Out" },
] as const

const Pipeline = () => {
  const { data: userDetails } = useUserDetails()
  const activePortfolioId = userDetails?.data?.activePortfolioId
  const { data, isLoading, isError } = useKanbanCards(activePortfolioId)
  const updateStage = useUpdateKanbanStage(activePortfolioId)

  const cardsByStage = useMemo(() => {
    if (!data) return {} as Record<string, KanbanCardType[]>
    return data.reduce<Record<string, KanbanCardType[]>>((acc, card) => {
      const stage = card.kanbanStage ?? "interested"
      if (!acc[stage]) acc[stage] = []
      acc[stage].push(card)
      return acc
    }, {})
  }, [data])

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result
    if (!destination) return
    if (destination.droppableId === source.droppableId) return

    updateStage.mutate(
      { id: Number(draggableId), kanbanStage: destination.droppableId },
      {
        onError: () => {
          toaster.create({
            title: "Move failed",
            description: "Could not update the deal stage. Please try again.",
            type: "error",
            duration: 4000,
          })
        },
      }
    )
  }

  const handleAcquiredRecovery = (id: number) => {
    updateStage.mutate(
      { id, kanbanStage: "acquired" },
      {
        onError: () => {
          toaster.create({
            title: "Move failed",
            description: "Could not mark as acquired. Please try again.",
            type: "error",
            duration: 4000,
          })
        },
      }
    )
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="50vh">
        <Spinner size="xl" />
      </Flex>
    )
  }

  if (isError) {
    return (
      <Flex justify="center" align="center" h="50vh">
        <Text color="red.500">Failed to load deals</Text>
      </Flex>
    )
  }

  return (
    <Box p={8}>
      <Heading size="xl" mb={2}>Pipeline</Heading>
      <Text color="gray.600" mb={6}>Track deals sourced by Greenflip as they move from interest through due diligence to acquisition.</Text>

      <DragDropContext onDragEnd={handleDragEnd}>
          <Flex gap={4} overflowX="auto" pb={4} minH="calc(100vh - 200px)">
            {COLUMNS.map((column) => {
              const cards = cardsByStage[column.id] ?? []
              return (
                <Box
                  key={column.id}
                  minW="260px"
                  w="260px"
                  bg="gray.50"
                  borderRadius="lg"
                  p={3}
                  flexShrink={0}
                >
                  <Text
                    fontWeight="bold"
                    fontSize="sm"
                    color="gray.600"
                    mb={3}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {column.title} ({cards.length})
                  </Text>
                  <Droppable
                    droppableId={column.id}
                    isDropDisabled={column.id === "missed_out"}
                  >
                    {(provided) => (
                      <VStack
                        gap={2}
                        align="stretch"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        minH="100px"
                      >
                        {cards.length === 0 ? (
                          <Text
                            fontSize="sm"
                            color="gray.400"
                            textAlign="center"
                            py={8}
                          >
                            No cards
                          </Text>
                        ) : (
                          cards.map((card, idx) => (
                            <Draggable
                              key={card.id}
                              draggableId={String(card.id)}
                              index={idx}
                              isDragDisabled={column.id === "missed_out"}
                            >
                              {(dragProvided) => (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                >
                                  <KanbanCard
                                    card={card}
                                    onAcquiredRecovery={
                                      column.id === "missed_out"
                                        ? handleAcquiredRecovery
                                        : undefined
                                    }
                                  />
                                </Box>
                              )}
                            </Draggable>
                          ))
                        )}
                        {provided.placeholder}
                      </VStack>
                    )}
                  </Droppable>
                </Box>
              )
            })}
          </Flex>
        </DragDropContext>
    </Box>
  )
}

export default Pipeline
